import React, { useState } from 'react';
import { Slide, SlideElement } from '../types';
import { X, Eye, EyeOff, Lock, Unlock, MoreVertical, GripVertical } from 'lucide-react';

interface LayerPanelProps {
  slide: Slide;
  isOpen: boolean;
  onClose: () => void;
  onElementSelect?: (elementId: string) => void;
  selectedElementId?: string;
  onElementUpdate?: (elementId: string, updates: Partial<SlideElement>) => void;
  isDarkMode?: boolean;
}

export const LayerPanel: React.FC<LayerPanelProps> = ({
  slide,
  isOpen,
  onClose,
  onElementSelect,
  selectedElementId,
  onElementUpdate,
  isDarkMode = false,
}) => {
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [dragOverElement, setDragOverElement] = useState<string | null>(null);
  
  if (!isOpen) {return null;}

  const handleToggleVisibility = (elementId: string, currentVisibility = true) => {
    onElementUpdate?.(elementId, { visible: !currentVisibility });
  };

  const handleToggleLock = (elementId: string, currentLocked = false) => {
    onElementUpdate?.(elementId, { locked: !currentLocked });
  };

  const handleDragStart = (e: React.DragEvent, elementId: string) => {
    setDraggedElement(elementId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, elementId: string) => {
    e.preventDefault();
    if (draggedElement && draggedElement !== elementId) {
      setDragOverElement(elementId);
    }
  };

  const handleDragLeave = () => {
    setDragOverElement(null);
  };

  const handleDrop = (e: React.DragEvent, targetElementId: string) => {
    e.preventDefault();
    
    if (!draggedElement || !slide.elements || draggedElement === targetElementId) {
      return;
    }

    const elements = [...slide.elements];
    const draggedIndex = elements.findIndex(el => el.id === draggedElement);
    const targetIndex = elements.findIndex(el => el.id === targetElementId);
    
    if (draggedIndex === -1 || targetIndex === -1) {return;}

    // Remove dragged element
    const [draggedEl] = elements.splice(draggedIndex, 1);
    
    // Insert at new position
    elements.splice(targetIndex, 0, draggedEl);
    
    // Update zIndex for all elements based on new order (top of list = highest z-index)
    const updatedElements = elements.map((el, index) => ({
      ...el,
      zIndex: elements.length - index // First element gets highest z-index
    }));

    // Update all elements with new zIndex
    updatedElements.forEach(el => {
      onElementUpdate?.(el.id, { zIndex: el.zIndex });
    });

    setDraggedElement(null);
    setDragOverElement(null);
  };

  const getElementTypeIcon = (type: string) => {
    switch (type) {
      case 'text': return '📝';
      case 'div': return '📋';
      case 'list': return '📋';
      case 'chart': return '📊';
      case 'image': return '🖼️';
      case 'shape': return '🔷';
      default: return '📄';
    }
  };

  // 안전하게 콘텐츠를 문자열로 변환하는 헬퍼 함수
  const safeGetContent = (content: unknown): string => {
    if (typeof content === 'string') {return content;}
    if (content === null || content === undefined) {return '';}
    
    if (typeof content === 'object') {
      if (Array.isArray(content)) {
        return content.map(item => {
          if (typeof item === 'string') {return item;}
          if (typeof item === 'object' && item !== null) {
            if (item.text) {return item.text;}
            if (item.title) {return item.title;}
            if (item.content) {return item.content;}
            if (item.description) {return item.description;}
            if (item.value) {return item.value;}
            const values = Object.values(item).filter(v => typeof v === 'string' && v.trim());
            if (values.length > 0) {return values.join(': ');}
          }
          return String(item);
        }).filter(item => item && item.trim()).join(', ');
      }
      
      // 일반적인 텍스트 속성들 확인
      if (content.text) {return String(content.text);}
      if (content.value) {return String(content.value);}
      if (content.title) {return String(content.title);}
      if (content.description) {return String(content.description);}
      if (content.content) {return safeGetContent(content.content);}
      
      // 단순한 객체라면 key-value 형태로 변환
      try {
        const values = Object.values(content).filter(v => 
          v !== null && 
          v !== undefined && 
          typeof v === 'string' && 
          v.trim().length > 0
        );
        
        if (values.length > 0) {
          return values.join(', ');
        }
        
        const entries = Object.entries(content);
        if (entries.length > 0 && entries.every(([key, value]) => typeof value !== 'object')) {
          return entries.map(([key, value]) => `${key}: ${value}`).join(', ');
        }
      } catch (e) {
        // 변환 실패 시 빈 문자열
      }
      
      return '';
    }
    
    return String(content);
  };

  const getElementDescription = (element: SlideElement) => {
    // 제목 요소인지 확인 (y 위치가 상단 15% 이내이고 폰트 크기가 큰 경우)
    const isTitle = element.type === 'text' && 
                   ((element.y || 0) < 0.15) && 
                   (element.fontSize > 20);
    
    if (isTitle) {
      // 제목 객체는 실제 제목 내용을 표시
      const titleContent = safeGetContent(element.content);
      return titleContent.substring(0, 30) || '제목';
    }
    
    switch (element.type) {
      case 'div': {
        const divContent = safeGetContent(element.content);
        return `DIV: ${divContent.substring(0, 20) || '빈 내용'}...`;
      }
      case 'list':
        return `목록: ${element.listItems?.length || 0}개 항목`;
      case 'chart':
        return `차트: ${element.chartData?.type || '알 수 없음'} 타입`;
      case 'text': {
        const textContent = safeGetContent(element.content);
        return `텍스트: ${textContent.substring(0, 20) || '빈 텍스트'}...`;
      }
      case 'image':
        return '이미지';
      case 'shape':
        return `도형: ${element.shapeType || '기본'}`;
      default:
        return `${element.type}: 알 수 없는 타입`;
    }
  };

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      {/* 패널 */}
      <div className={`w-80 h-full ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-xl border-l border-gray-200 dark:border-gray-700 flex flex-col`}>
        {/* 헤더 */}
        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className="text-lg font-semibold">레이어 패널</h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 슬라이드 정보 */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h4 className="font-medium mb-2">{slide.title}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            총 {slide.elements?.length || 0}개 요소
          </p>
        </div>

        {/* 요소 목록 */}
        <div className="flex-1 overflow-y-auto">
          {!slide.elements || slide.elements.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              요소가 없습니다
            </div>
          ) : (
            <div className="p-2">
              {/* Sort elements by z-index (highest first) for display */}
              {[...slide.elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0)).map((element, index) => (
                <div
                  key={element.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, element.id)}
                  onDragOver={(e) => handleDragOver(e, element.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, element.id)}
                  className={`p-3 mb-2 rounded-lg cursor-move transition-colors ${
                    selectedElementId === element.id
                      ? isDarkMode 
                        ? 'bg-blue-900 border-blue-600' 
                        : 'bg-blue-50 border-blue-300'
                      : isDarkMode 
                        ? 'bg-gray-700 hover:bg-gray-600' 
                        : 'bg-gray-50 hover:bg-gray-100'
                  } border ${
                    dragOverElement === element.id
                      ? 'border-blue-500 border-2'
                      : ''
                  }`}
                  onClick={() => onElementSelect?.(element.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <span className="text-lg">{getElementTypeIcon(element.type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          레이어 {index + 1} (Z: {element.zIndex || 1})
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {getElementDescription(element)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {/* 가시성 토글 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleVisibility(element.id, element.visible);
                        }}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title={element.visible !== false ? "숨기기" : "보이기"}
                      >
                        {element.visible !== false ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                      </button>

                      {/* 잠금 토글 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleLock(element.id, element.locked);
                        }}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title={element.locked ? "잠금 해제" : "잠금"}
                      >
                        {element.locked ? (
                          <Lock className="w-4 h-4 text-red-500" />
                        ) : (
                          <Unlock className="w-4 h-4" />
                        )}
                      </button>

                      {/* 더보기 메뉴 */}
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* 요소 세부 정보 */}
                  {selectedElementId === element.id && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="font-medium">위치:</span> 
                          <span className="ml-1">{Math.round(element.x)}, {Math.round(element.y)}</span>
                        </div>
                        <div>
                          <span className="font-medium">크기:</span> 
                          <span className="ml-1">{Math.round(element.width)} × {Math.round(element.height)}</span>
                        </div>
                        {element.fontSize && (
                          <div>
                            <span className="font-medium">폰트:</span> 
                            <span className="ml-1">{Math.round(element.fontSize)}px</span>
                          </div>
                        )}
                        {element.zIndex && (
                          <div>
                            <span className="font-medium">Z-Index:</span> 
                            <span className="ml-1">{element.zIndex}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* JSON 미리보기 */}
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          JSON 데이터 보기
                        </summary>
                        <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-x-auto">
                          {JSON.stringify(element, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p>💡 팁: 요소를 드래그하여 순서를 변경하세요. 위쪽일수록 앞에 배치됩니다.</p>
          </div>
        </div>
      </div>
    </div>
  );
};